import { type FC, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import pluralize from 'pluralize'
import { Link } from 'react-router-dom'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { type SeatPurchase, useBuyAndPromoteInvitesMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { readBatchBuyResult } from './buyResult'
import { SeatPurchaseSummary } from './SeatPurchaseSummary'
import { useRequestedSeatsSnapshot } from './useRequestedSeatsSnapshot'
import { useSeatPurchasePreview } from './useSeatPurchasePreview'

export interface BuyRequestedSeatsModalProps {
  onHide: () => void
  onSeatsChanged: () => void
}

const NOTHING_TO_BUY: SeatPurchase = { general: 0, sidecar: 0 }

export const BuyRequestedSeatsModal: FC<BuyRequestedSeatsModalProps> = ({ onHide, onSeatsChanged }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const [buyAndPromoteInvites, { isLoading }] = useBuyAndPromoteInvitesMutation()
  const [error, setError] = useState<string | null>(null)

  const { invitationIds, total, purchase, isLoading: snapshotLoading } = useRequestedSeatsSnapshot()
  const preview = useSeatPurchasePreview(purchase ?? NOTHING_TO_BUY)

  const seatsToBuy = purchase ? purchase.general + purchase.sidecar : 0

  const handleConfirm = async () => {
    if (!purchase) {
      return
    }

    setError(null)
    let verdict
    try {
      const response = await buyAndPromoteInvites({ accountId, invitationIds, purchase }).unwrap()
      verdict = readBatchBuyResult(response, invitationIds)
    } catch {
      verdict = readBatchBuyResult(undefined, invitationIds)
    }

    onSeatsChanged()

    if (verdict.bought) {
      trackingClient.track({
        eventName: TrackEvent.SEATS_PURCHASED,
        eventDetails: { general: verdict.purchased.general, sidecar: verdict.purchased.sidecar, invitations: verdict.sent },
      })
    }

    if (verdict.ok) {
      dispatch(addToast({ variant: 'success', title: `${pluralize('Invitation', verdict.sent)} sent` }))
    }

    if (verdict.closesModal) {
      onHide()
      return
    }

    setError(verdict.message ?? null)
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Buy requested seats</h4>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <div className='paragraph-medium bold'>
            {total} {pluralize('invitation', total)} {total === 1 ? 'is' : 'are'} waiting
          </div>
          <div className='paragraph-medium pb-2'>
            {snapshotLoading || seatsToBuy > 0
              ? "There aren't enough seats to send them. Buying the seats below sends all of them automatically."
              : 'There are enough free seats for every request. You can send them from the Requested list.'}
          </div>
          <SeatPurchaseSummary preview={preview} />
          <div className='paragraph-small zocks-text-gray-600 pt-3'>
            You can also manage your seats on the <Link to='/account/plan'>Plan page</Link>.
          </div>
          {error ? <FormFeedback>{error}</FormFeedback> : null}
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      show
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions
      onFooterPrimaryAction={handleConfirm}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={snapshotLoading || seatsToBuy === 0}
      footerPrimaryActionTitle='Confirm'
    />
  )
}
